// src/view/Project/DataRoom/DataRoom.tsx
import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Breadcrumbs,
  Link as MuiLink,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  LinearProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Audiotrack as AudioIcon,
  CreateNewFolder as NewFolderIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  MoreVert as MoreIcon,
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FolderZip as FolderZipIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { useSnackbar } from 'src/view/components/SnackbarService';
import { SnackbarType } from 'src/view/components/SnackbarService';
import {
  dataRoomApi,
  uploadToSession,
  TreeNode,
  DeploymentFile,
} from 'src/api/dataRoom';
import FilePreview from './FilePreview';

interface Crumb {
  id: string | null; // null = root
  name: string;
}

interface DataRoomProps {
  projectIdOverride?: string;
}

const formatBytes = (n?: number | null): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fileIconFor = (contentType?: string | null) => {
  if (contentType?.startsWith('image/')) return <ImageIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />;
  if (contentType?.startsWith('audio/')) return <AudioIcon sx={{ color: '#3B82F6', fontSize: 20 }} />;
  return <FileIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />;
};

const DataRoom = ({ projectIdOverride }: DataRoomProps) => {
  const { id: routeProjectId } = useParams<{ id: string }>();
  const projectId = projectIdOverride || routeProjectId;

  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: 'Data Room Home' }]);
  const [deploymentFiles, setDeploymentFiles] = useState<DeploymentFile[] | null>(null);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuNode, setMenuNode] = useState<TreeNode | null>(null);

  const [uploads, setUploads] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewNode, setPreviewNode] = useState<{ name: string; contentType?: string | null } | null>(null);

  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await dataRoomApi.getTree(projectId);
      setNodes(res.nodes);
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to load Data Room', type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  }, [projectId, showSnackbar]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const currentCrumb = crumbs[crumbs.length - 1];
  const currentId = currentCrumb.id;

  const currentNode = useMemo(
    () => nodes.find((n) => n.id === currentId) || null,
    [nodes, currentId],
  );

  const inDeploymentBranch =
    currentId === 'deployment-root' ||
    (currentId?.startsWith('dep:') ?? false) ||
    currentNode?.branch === 'deployment';

  const writeContext = useMemo(() => {
    if (inDeploymentBranch) return null;
    if (!currentNode) {
      return { scope: 'project' as const, site_id: undefined, parent_id: undefined };
    }
    return {
      scope: (currentNode.scope || 'project') as 'project' | 'site',
      site_id: currentNode.site_id || undefined,
      parent_id: currentNode.node_type === 'folder' ? currentNode.id : currentNode.parent_id || undefined,
    };
  }, [currentNode, inDeploymentBranch]);

  const children = useMemo<TreeNode[]>(() => {
    if (currentId?.startsWith('dep:sub:')) return [];

    let items: TreeNode[] = [];
    if (currentId === null) {
      const dep = nodes.filter((n) => n.id === 'deployment-root');
      const custom = nodes.filter(
        (n) => n.branch === 'custom' && n.scope === 'project' && !n.parent_id,
      );
      items = [...dep, ...custom];
    } else if (currentId === 'deployment-root' || currentId.startsWith('dep:')) {
      items = nodes.filter((n) => n.branch === 'deployment' && n.dep_parent === currentId);
    } else {
      items = nodes.filter((n) => n.branch === 'custom' && n.parent_id === currentId);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((n) => n.name.toLowerCase().includes(q));
    }

    return items;
  }, [nodes, currentId, searchQuery]);

  useEffect(() => {
    const fetchDeploymentFiles = async () => {
      if (!projectId || !currentId?.startsWith('dep:sub:')) {
        setDeploymentFiles(null);
        return;
      }
      const rest = currentId.slice('dep:sub:'.length);
      const sep = rest.lastIndexOf(':');
      const deploymentId = rest.slice(0, sep);
      const folder = rest.slice(sep + 1) as 'audios' | 'images';
      setDeploymentFiles(null);
      try {
        const res = await dataRoomApi.listDeploymentFiles(projectId, deploymentId, folder);
        setDeploymentFiles(res.files);
      } catch (e: any) {
        showSnackbar({ message: e?.message || 'Failed to list deployment files', type: SnackbarType.ERROR });
        setDeploymentFiles([]);
      }
    };
    fetchDeploymentFiles();
  }, [projectId, currentId, showSnackbar]);

  const enterFolder = (node: TreeNode) => {
    setCrumbs((c) => [...c, { id: node.id, name: node.name }]);
  };

  const goToCrumb = (index: number) => {
    setCrumbs((c) => c.slice(0, index + 1));
  };

  const openFile = async (opts: { gcs_path: string; name: string; content_type?: string | null }) => {
    if (!projectId) return;
    try {
      const { download_url } = await dataRoomApi.getDownloadUrl(projectId, opts.gcs_path);
      setPreviewUrl(download_url);
      setPreviewNode({ name: opts.name, contentType: opts.content_type });
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Could not access file URL', type: SnackbarType.ERROR });
    }
  };

  const handleCreateFolder = async () => {
    if (!projectId || !writeContext) return;
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await dataRoomApi.createFolder(projectId, {
        name,
        scope: writeContext.scope,
        site_id: writeContext.site_id,
        parent_id: writeContext.parent_id,
      });
      showSnackbar({ message: 'Folder created successfully', type: SnackbarType.SUCCESS });
      setNewFolderOpen(false);
      setNewFolderName('');
      await loadTree();
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to create folder', type: SnackbarType.ERROR });
    }
  };

  const handleFilesChosen = async (files: FileList | null) => {
    if (!projectId || !writeContext || !files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const key = file.name;
      setUploads((u) => ({ ...u, [key]: 0 }));
      try {
        const { upload_session_uri, gcs_path } = await dataRoomApi.initiateUpload(projectId, {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          scope: writeContext.scope,
          site_id: writeContext.site_id,
          parent_id: writeContext.parent_id,
        });
        await uploadToSession(upload_session_uri, file, (pct: number) =>
          setUploads((u) => ({ ...u, [key]: pct })),
        );
        await dataRoomApi.finalizeUpload(projectId, {
          file_name: file.name,
          gcs_path,
          content_type: file.type || undefined,
          scope: writeContext.scope,
          site_id: writeContext.site_id,
          parent_id: writeContext.parent_id,
        });
        showSnackbar({ message: `Uploaded ${file.name}`, type: SnackbarType.SUCCESS });
      } catch (e: any) {
        showSnackbar({ message: e?.message || `Failed to upload ${file.name}`, type: SnackbarType.ERROR });
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[key];
          return next;
        });
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    await loadTree();
  };

  const handleDelete = async (node: TreeNode) => {
    if (!projectId) return;
    setMenuAnchor(null);
    if (!window.confirm(`Are you sure you want to delete "${node.name}"?`)) {
      return;
    }
    try {
      await dataRoomApi.deleteNode(projectId, node.id);
      showSnackbar({ message: 'Item deleted', type: SnackbarType.SUCCESS });
      await loadTree();
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to delete item', type: SnackbarType.ERROR });
    }
  };

  const canWriteHere = !inDeploymentBranch && !!writeContext;
  const activeUploads = Object.entries(uploads);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#070809',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        boxSizing: 'border-box',
        overflowY: 'auto', '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: '#1a1a1a' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(16, 185, 129, 0.5)', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#10b981' },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FolderZipIcon sx={{ color: '#10B981', fontSize: 26 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.5px', color: '#ffffff' }}>
            Data Room
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            placeholder="Search files..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: 'rgba(255, 255, 255, 0.04)',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '12px',
                fieldset: { borderColor: 'rgba(255, 255, 255, 0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(16, 185, 129, 0.4) !important' },
                '&.Mui-focused fieldset': { borderColor: '#10B981 !important' },
              },
            }}
          />

          <Button
            variant="outlined"
            startIcon={<NewFolderIcon />}
            disabled={!canWriteHere}
            onClick={() => setNewFolderOpen(true)}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.85)',
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              borderRadius: '6px',
              px: 2,
              '&:hover': {
                borderColor: '#10B981',
                color: '#10B981',
                bgcolor: 'rgba(16, 185, 129, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.25)',
              },
            }}
          >
            New Folder
          </Button>

          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={!canWriteHere}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              bgcolor: '#10B981',
              color: '#000000',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              borderRadius: '6px',
              px: 2,
              '&:hover': {
                bgcolor: '#059669',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Upload
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => handleFilesChosen(e.target.files)}
          />
        </Box>
      </Box>

      {/* Breadcrumbs Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          p: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
        }}
      >
        {crumbs.length > 1 && (
          <IconButton
            size="small"
            onClick={() => goToCrumb(crumbs.length - 2)}
            sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.6)' }}
          >
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
          {crumbs.map((c, i) => (
            <MuiLink
              key={`${c.id}-${i}`}
              component="button"
              underline="hover"
              sx={{
                color: i === crumbs.length - 1 ? '#10B981' : 'rgba(255, 255, 255, 0.7)',
                fontWeight: i === crumbs.length - 1 ? 700 : 500,
                fontSize: '13px',
                cursor: 'pointer',
              }}
              onClick={() => goToCrumb(i)}
            >
              {c.name}
            </MuiLink>
          ))}
        </Breadcrumbs>
      </Box>

      {/* Read-only chip indicator for deployment folder */}
      {inDeploymentBranch && (
        <Chip
          icon={<LockIcon sx={{ fontSize: '14px !important', color: '#F59E0B !important' }} />}
          label="Read-only — Deployment telemetry files managed automatically"
          size="small"
          sx={{
            mb: 2,
            alignSelf: 'flex-start',
            bgcolor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            fontSize: '11px',
            fontWeight: 600,
          }}
        />
      )}

      {/* Upload progress */}
      {activeUploads.length > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '8px' }}>
          {activeUploads.map(([name, pct]) => (
            <Box key={name} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600 }}>{name}</Typography>
                <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700 }}>{pct}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': { bgcolor: '#10B981' },
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Content table / listing */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#10B981' }} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            bgcolor: 'rgba(10, 11, 14, 0.75)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
            p: 1.5,
          }}
        >
          {currentId?.startsWith('dep:sub:') ? (
            deploymentFiles === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} sx={{ color: '#10B981' }} />
              </Box>
            ) : deploymentFiles.length === 0 ? (
              <Typography sx={{ py: 6, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                No deployment files available.
              </Typography>
            ) : (
              deploymentFiles.map((f) => (
                <DataRoomRow
                  key={f.gcs_path}
                  icon={fileIconFor(f.content_type)}
                  name={f.name}
                  meta={formatBytes(f.size_bytes)}
                  onOpen={() => openFile({ gcs_path: f.gcs_path, name: f.name, content_type: f.content_type })}
                />
              ))
            )
          ) : children.length === 0 ? (
            <Typography sx={{ py: 6, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
              {inDeploymentBranch
                ? 'No items found in this deployment folder.'
                : 'This folder is empty. Click "Upload" or "New Folder" to add items.'}
            </Typography>
          ) : (
            children.map((node) => (
              <DataRoomRow
                key={node.id}
                icon={
                  node.node_type === 'folder' ? (
                    <FolderIcon sx={{ color: '#F59E0B', fontSize: 22 }} />
                  ) : (
                    fileIconFor(node.content_type)
                  )
                }
                name={node.name}
                meta={node.node_type === 'file' ? formatBytes(node.size_bytes) : undefined}
                lock={node.branch === 'deployment'}
                onOpen={() => {
                  if (node.node_type === 'folder') enterFolder(node);
                  else if (node.gcs_path)
                    openFile({ gcs_path: node.gcs_path, name: node.name, content_type: node.content_type });
                }}
                onMenu={
                  node.branch === 'custom'
                    ? (el) => {
                        setMenuAnchor(el);
                        setMenuNode(node);
                      }
                    : undefined
                }
              />
            ))
          )}
        </Box>
      )}

      {/* Row context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: '#0d0f12',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          },
        }}
      >
        <MenuItem
          onClick={() => menuNode && handleDelete(menuNode)}
          sx={{ color: '#EF4444', fontSize: '13px', fontWeight: 600 }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create new folder dialog */}
      <Dialog
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: '#0d0f12',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#ffffff' }}>
          Create New Folder
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{
              mt: 1,
              input: { color: '#ffffff', fontSize: '13px' },
              label: { color: 'rgba(255, 255, 255, 0.6)' },
              fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
              '&:hover fieldset': { borderColor: '#10B981 !important' },
              '&.Mui-focused fieldset': { borderColor: '#10B981 !important' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNewFolderOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFolder}
            sx={{ bgcolor: '#10B981', color: '#000000', fontWeight: 700, fontSize: '12px', '&:hover': { bgcolor: '#059669' } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Preview modal */}
      <FilePreview
        open={!!previewUrl}
        url={previewUrl}
        name={previewNode?.name || ''}
        contentType={previewNode?.contentType}
        onClose={() => {
          setPreviewUrl(null);
          setPreviewNode(null);
        }}
      />
    </Box>
  );
};

const DataRoomRow = ({
  icon,
  name,
  meta,
  lock,
  onOpen,
  onMenu,
}: {
  icon: ReactNode;
  name: string;
  meta?: string;
  lock?: boolean;
  onOpen: () => void;
  onMenu?: (el: HTMLElement) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderRadius: '6px',
      cursor: 'pointer',
      bgcolor: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.03)',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderColor: 'rgba(16, 185, 129, 0.3)',
      },
    }}
    onClick={onOpen}
  >
    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }} noWrap>
      {name}
    </Typography>
    {lock && (
      <Tooltip title="Read-only system data">
        <LockIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '15px', mr: 1.5 }} />
      </Tooltip>
    )}
    {meta && (
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', mr: 1.5, fontSize: '11px' }}>
        {meta}
      </Typography>
    )}
    {onMenu ? (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onMenu(e.currentTarget);
        }}
        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
      >
        <MoreIcon fontSize="small" />
      </IconButton>
    ) : (
      <DownloadIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.3)', visibility: 'hidden' }} />
    )}
  </Box>
);

export default DataRoom;
